import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { authFetch } from '@verantos-dev/core-utils';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { analysisKeys } from './queryKeys';
import type { AnalysisResults } from './types';
import { useAnalysisProfileQuery } from './useAnalysisProfileQuery';
import { hasProcessingExceededLimit } from '../utils/processingLimit';
import { getProcessingStage } from '../utils/processingStage';

// How often the results are re-read while a run is PROCESSING and computing its
// tables. The worker persists each column's cells as its query finishes and this
// endpoint serves the run's cells so far, so polling shows the table filling in as
// columns complete instead of only once the whole run ends.
export const PROCESSING_RESULTS_POLL_INTERVAL_MS = 3000;

// While the run is still preparing the analytical dataset no cell exists to show, so
// the poll only has to notice the move to the next stage, and can be slower.
export const DATASETS_STAGE_RESULTS_POLL_INTERVAL_MS = 6000;

interface UseAnalysisResultsQueryArgs {
  projectId: string;
  analysisProfileId: string;
}

// The latest version's table shells populated with the latest run's cells.
export const useAnalysisResultsQuery = ({
  projectId,
  analysisProfileId,
}: UseAnalysisResultsQueryArgs) => {
  const queryClient = useQueryClient();
  const { data: profileEnvelope } = useAnalysisProfileQuery({ projectId, analysisProfileId });
  const isProcessing = profileEnvelope?.data.latestStatus === 'PROCESSING';

  const query = useQuery({
    queryKey: analysisKeys.results(analysisProfileId),
    queryFn: () =>
      authFetch<ObjectResponse<AnalysisResults>>(
        `/analyses/${encodeURIComponent(projectId)}` +
          `/analysis-profile/${encodeURIComponent(analysisProfileId)}/results`
      ),
    enabled: projectId.length > 0 && analysisProfileId.length > 0,
    retry: false,
    // Evaluated after every fetch, so polling stops within one interval of the run
    // passing the poll limit (no render is needed when the limit passes), and the
    // pace follows the stage the last response reported. Until a response shows the
    // run processing (right after it was dispatched) it polls at the faster pace.
    refetchInterval: (current) => {
      if (!isProcessing || hasProcessingExceededLimit(queryClient, analysisProfileId)) return false;
      return getProcessingStage(current.state.data?.data) === 'DATASETS'
        ? DATASETS_STAGE_RESULTS_POLL_INTERVAL_MS
        : PROCESSING_RESULTS_POLL_INTERVAL_MS;
    },
  });

  // The run's status in the results ends in the same commit as the profile's, so
  // when it leaves PROCESSING the profile is re-read once, instead of the profile
  // having to be polled as often as the results to notice.
  const resultsStatus = query.data?.data.status;
  const previousResultsStatus = useRef(resultsStatus);
  useEffect(() => {
    if (previousResultsStatus.current === 'PROCESSING' && resultsStatus !== 'PROCESSING') {
      void queryClient.invalidateQueries(
        { queryKey: analysisKeys.profile(analysisProfileId) },
        { cancelRefetch: false }
      );
    }
    previousResultsStatus.current = resultsStatus;
  }, [resultsStatus, analysisProfileId, queryClient]);

  return query;
};
